import { FileText, UploadSimpleIcon } from "@phosphor-icons/react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { PaginatedResponse } from "@/api/types";
import { GenerateReportDialog } from "@/components/meters/generate-report-dialog";
import { ImportMetersDialog } from "@/components/meters/import-meters-dialog";
import { MeterBulkActions } from "@/components/meters/meter-bulk-actions";
import type { Column } from "@/components/meters/meter-table";
import { MeterTable } from "@/components/meters/meter-table";
import { Button } from "@/components/ui/button";
import { useDebounce } from "@/hooks/use-debounce";
import { useMeterSelection } from "@/hooks/use-meter-selection";
import i18n from "@/i18n";

export interface MeterTabProps {
	page: number;
	pageSize: number;
	setPage: (p: number) => void;
	setPageSize: (size: number) => void;
	search: string;
	setSearch: (s: string) => void;
	ordering: string;
	setOrdering: (o: string) => void;
	districtId: number | null;
}

interface GenericMeterTabProps<T extends { serial_number: string }>
	extends MeterTabProps {
	getColumns: (
		t: ReturnType<typeof useTranslation>["t"],
		locale: string,
	) => Column<T>[];
	useTableQuery: (params: {
		page: number;
		page_size: number;
		search?: string;
		ordering?: string;
		district_id?: number;
	}) => {
		data: PaginatedResponse<T> | undefined;
		isLoading: boolean;
		isError: boolean;
		refetch: () => void;
	};
	headerActions?: ReactNode;
}

export function GenericMeterTab<T extends { serial_number: string }>({
	page,
	pageSize,
	setPage,
	setPageSize,
	search,
	setSearch,
	ordering,
	setOrdering,
	districtId,
	getColumns,
	useTableQuery,
}: GenericMeterTabProps<T>) {
	const { t } = useTranslation();
	const [inputValue, setInputValue] = useState(search);
	const [importOpen, setImportOpen] = useState(false);
	const [reportOpen, setReportOpen] = useState(false);
	const debouncedSearch = useDebounce(inputValue);
	const setSearchRef = useRef(setSearch);
	useEffect(() => {
		setSearchRef.current = setSearch;
	});
	useEffect(() => {
		setSearchRef.current(debouncedSearch);
	}, [debouncedSearch]);

	const locale = i18n.language;
	const columns = useMemo(() => getColumns(t, locale), [getColumns, t, locale]);

	const { data, isLoading, isError, refetch } = useTableQuery({
		page,
		page_size: pageSize,
		search: debouncedSearch || undefined,
		ordering: ordering || undefined,
		district_id: districtId ?? undefined,
	});

	const rows = data?.results ?? [];
	const {
		selectedSerials,
		toggleRow,
		toggleAll,
		isAllSelected,
		isIndeterminate,
		clearSelection,
	} = useMeterSelection(rows, [
		page,
		pageSize,
		debouncedSearch,
		ordering,
		districtId,
	]);

	return (
		<MeterTable
			data={data}
			columns={columns}
			loading={isLoading}
			isError={isError}
			onRetry={refetch}
			page={page}
			pageSize={pageSize}
			onPageChange={setPage}
			onPageSizeChange={setPageSize}
			search={inputValue}
			onSearchChange={setInputValue}
			ordering={ordering}
			onOrderingChange={setOrdering}
			selectedKeys={selectedSerials}
			onSelectKey={toggleRow}
			onSelectAll={toggleAll}
			isAllSelected={isAllSelected}
			isIndeterminate={isIndeterminate}
			headerActions={
				<div className="flex flex-wrap items-center gap-2">
					<Button
						variant="outline"
						onClick={() => setImportOpen(true)}
						disabled={!districtId}
					>
						<UploadSimpleIcon aria-hidden="true" />
						{t("meterImport.triggerButton")}
					</Button>
					<ImportMetersDialog
						open={importOpen}
						onOpenChange={setImportOpen}
						districtId={districtId}
					/>
					<Button
						variant="default"
						className="gap-1.5"
						onClick={() => setReportOpen(true)}
					>
						<FileText className="size-4" aria-hidden="true" />
						{t("report.generate")}
					</Button>
					<GenerateReportDialog
						open={reportOpen}
						onOpenChange={setReportOpen}
					/>
				</div>
			}
			selectionBar={
				<MeterBulkActions
					selectedSerials={selectedSerials}
					onSuccess={clearSelection}
					onClear={clearSelection}
				/>
			}
		/>
	);
}
